"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import { getUserLogout } from "@/redux/user/userSlice";
import { resetAnonymousId } from "@/utils/anonymousId";
import { getApiToken } from "@/redux/config/configSlice";
import { initiateToken } from "@/utils/services/tokenService";
import useCartCount from "@/hooks/useCartCount";
import PartnerAuthModals from "@/components/Partner/PartnerAuthModals";

const AccountIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 0C3.36 0 0 3.36 0 7.5C0 11.64 3.36 15 7.5 15C11.64 15 15 11.64 15 7.5C15 3.36 11.64 0 7.5 0ZM4.0125 12.375C4.995 11.67 6.195 11.25 7.5 11.25C8.805 11.25 10.005 11.67 10.9875 12.375C10.005 13.08 8.805 13.5 7.5 13.5C6.195 13.5 4.995 13.08 4.0125 12.375ZM12.105 11.34C10.8375 10.35 9.24 9.75 7.5 9.75C5.76 9.75 4.1625 10.35 2.895 11.34C2.025 10.2975 1.5 8.9625 1.5 7.5C1.5 4.185 4.185 1.5 7.5 1.5C10.815 1.5 13.5 4.185 13.5 7.5C13.5 8.9625 12.975 10.2975 12.105 11.34Z" fill="#666666"/>
    <path d="M7.5 3C6.0525 3 4.875 4.1775 4.875 5.625C4.875 7.0725 6.0525 8.25 7.5 8.25C8.9475 8.25 10.125 7.0725 10.125 5.625C10.125 4.1775 8.9475 3 7.5 3ZM7.5 6.75C6.8775 6.75 6.375 6.2475 6.375 5.625C6.375 5.0025 6.8775 4.5 7.5 4.5C8.1225 4.5 8.625 5.0025 8.625 5.625C8.625 6.2475 8.1225 6.75 7.5 6.75Z" fill="#666666"/>
  </svg>
);

/**
 * Floating bottom navigation for partner white-label storefronts (mobile only).
 *
 * The global Sukuto MobileBar lives inside the global Header, which PartnerLayout
 * replaces with PartnerHeader — so partner routes had no bottom nav. This is the
 * partner-scoped equivalent: every destination stays inside the storefront (or
 * the branded shared funnel) so the white-label experience isn't broken.
 *
 * - Home   → the partner landing page (basePath)
 * - Search → the partner-scoped search results
 * - Cart   → the shared /cart funnel (branded via JourneyChrome from the origin
 *            cookie); shows the live cart-count badge
 * - Account→ the customer's Sukuto dashboard (the platform account hub)
 *
 * Mirrors the global bar's styling, scroll-to-hide behaviour, and the
 * `mobileSearch` guard (so it doesn't overlap the mobile search overlay's own
 * action bar). lg:hidden — desktop uses the header only.
 */
const Tab = ({ active, children }) => (
  <li
    className={`relative font-light text-[11px] flex flex-col flex-1 items-center justify-center cursor-pointer ${active ? "!text-primary-deep !font-medium [&_svg_path]:fill-primary-deep [&_svg_path]:stroke-primary-deep" : "text-textMuted"}`}
  >
    {children}
  </li>
);

const PartnerMobileBar = ({ basePath }) => {
  const pathname = usePathname();
  const router = useRouter();
  const dispatch = useDispatch();
  const { totalCartCount, isCartLoaded } = useSelector((state) => state.order);
  const { mobileSearch } = useSelector((state) => state.search);
  const { isAuthenticated } = useSelector((state) => state.user);
  const refetchCart = useCartCount();
  const [navHidden, setNavHidden] = useState(false);
  const [showAcc, setShowAcc] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [loginType, setLoginType] = useState();
  // isAuthenticated comes from redux-persist (client-only); the server render is
  // always logged-out. Gate on a mount flag so the first client render matches
  // the server HTML (guest state) and React doesn't throw a hydration mismatch.
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount flag for SSR hydration parity
  useEffect(() => setMounted(true), []);
  const authed = mounted && isAuthenticated;

  // Mirrors the global header logout (clear user state → mint a fresh guest
  // token → refetch cart), but redirects to the storefront home instead of "/"
  // so the white-label journey isn't broken.
  const handleLogout = async () => {
    setShowAcc(false);
    dispatch(getUserLogout());
    resetAnonymousId();
    // Actually END the session: clear the user JWT immediately, then swap in a
    // fresh guest token — otherwise the never-expiring user JWT keeps riding
    // every request after "logout" (shared-device misattribution + the next
    // login inheriting this account's cart).
    dispatch(getApiToken(""));
    const newToken = await initiateToken();
    if (newToken) {
      dispatch(getApiToken(newToken));
      refetchCart();
    }
    router.push(`${basePath}/`);
  };

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const currentScrollY = window.scrollY;
        const delta = currentScrollY - lastScrollY;
        if (Math.abs(delta) > 5) {
          const shouldHide = delta > 0;
          setNavHidden((prev) => (prev === shouldHide ? prev : shouldHide));
        }
        lastScrollY = currentScrollY;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  if (mobileSearch) return null;

  const home = `${basePath}/`;
  const search = `${basePath}/search`;
  const isHome = pathname === basePath || pathname === home;
  const isSearch = pathname === search || pathname === `${search}/`;
  const isCart = pathname === "/cart" || pathname === "/cart/";
  const isAccount = pathname.startsWith("/user");

  return (
    <div
      className={`fixed lg:hidden bottom-[18px] left-1/2 z-[51] bg-white py-3 px-4 border border-gray-200 rounded-full shadow-sticky max-w-[350px] w-[calc(100%-32px)] transition-transform duration-300 ease-in-out ${navHidden ? "-translate-x-1/2 translate-y-[calc(100%+40px)]" : "-translate-x-1/2 translate-y-0"}`}
    >
      <ul className="flex [&_svg]:mx-auto [&_svg]:mb-1">
        <Tab active={isHome}>
          <Link href={home} aria-label="Storefront home">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.07143 14.0499H5.02714V8.45959H9.97286V14.0499H13.9286V5.49911L7.5 1.18761L1.07143 5.49911V14.0499ZM0 15V5.02407L7.5 0L15 5.02407V15H8.90143V9.40968H6.09857V15H0Z" fill="#666666"/>
            </svg>
            Home
          </Link>
        </Tab>

        <Tab active={isSearch}>
          <Link href={search} aria-label="Search vehicles">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M6.25 1.5a4.75 4.75 0 1 0 2.94 8.48l3.66 3.66a.75.75 0 1 0 1.06-1.06l-3.66-3.66A4.75 4.75 0 0 0 6.25 1.5ZM3 6.25a3.25 3.25 0 1 1 6.5 0 3.25 3.25 0 0 1-6.5 0Z" fill="#666666"/>
            </svg>
            Search
          </Link>
        </Tab>

        <Tab active={isCart}>
          <Link href="/cart" aria-label="Your cart">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.9084 8.25C11.4707 8.25 11.9656 7.9425 12.2205 7.4775L14.9045 2.61C15.1819 2.115 14.822 1.5 14.2522 1.5H3.15633L2.45159 0H0V1.5H1.49944L4.19844 7.1925L3.18632 9.0225C2.63902 10.0275 3.35875 11.25 4.49833 11.25H13.495V9.75H4.49833L5.32302 8.25H10.9084ZM3.86856 3H12.9777L10.9084 6.75H5.6454L3.86856 3ZM4.49833 12C3.67364 12 3.00638 12.675 3.00638 13.5C3.00638 14.325 3.67364 15 4.49833 15C5.32302 15 5.99777 14.325 5.99777 13.5C5.99777 12.675 5.32302 12 4.49833 12ZM11.9955 12C11.1709 12 10.5036 12.675 10.5036 13.5C10.5036 14.325 11.1709 15 11.9955 15C12.8202 15 13.495 14.325 13.495 13.5C13.495 12.675 12.8202 12 11.9955 12Z" fill="#666666"/>
            </svg>
            Cart
            {isCartLoaded && totalCartCount > 0 && (
              <span className="absolute top-0 right-[18%] bg-red-500 min-w-[18px] min-h-[18px] p-0.5 flex items-center justify-center text-white rounded-full text-center text-[10px] font-bold leading-none border-[1.5px] border-white">{totalCartCount}</span>
            )}
          </Link>
        </Tab>

        <Tab active={isAccount}>
          {/*
            The anchor + href stays in the markup (crawlable, works without JS).
            Client-side we intercept the tap: guests open the login modal (and
            stay on the storefront); signed-in users open an account sheet with
            links to their dashboard and a "Log Out" action — mirroring the
            global MobileBar drawer.
          */}
          <Link
            href="/user/dashboard"
            aria-label={authed ? "Your account" : "Login or sign up"}
            aria-haspopup="dialog"
            onClick={(e) => {
              e.preventDefault();
              if (authed) {
                setShowAcc(true);
              } else {
                setIsLoginModalOpen(true);
              }
            }}
          >
            <AccountIcon />
            Account
          </Link>
        </Tab>
      </ul>

      {showAcc && authed && (
        <>
          <div
            className="fixed inset-0 z-[52] bg-black/50 animate-[mb-fade-in_0.2s_ease-out]"
            onClick={() => setShowAcc(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[53] bg-white rounded-t-3xl pb-[100px] animate-[mb-slide-up_0.3s_cubic-bezier(0.32,0.72,0,1)] shadow-modal will-change-transform"
            role="dialog"
            aria-label="Account"
          >
            <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mt-2.5" />
            <div className="flex items-center justify-between px-6 pt-4 pb-3">
              <span className="text-[17px] font-semibold text-gray-900 tracking-tight">Account</span>
              <button
                type="button"
                className="w-9 h-9 flex items-center justify-center bg-transparent border-none cursor-pointer rounded-full transition-colors text-gray-500 hover:bg-gray-100 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:m-0"
                onClick={() => setShowAcc(false)}
                aria-label="Close account menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="h-px bg-gray-200 mx-6" />
            <nav className="flex flex-col gap-0.5 p-2 px-3">
              <Link
                href="/user/dashboard"
                className="flex items-center gap-3.5 py-3.5 px-3 rounded-md text-gray-700 text-[15px] font-medium tracking-wide no-underline cursor-pointer active:bg-red-50"
                onClick={() => setShowAcc(false)}
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 border border-gray-200 shrink-0 text-gray-500 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:m-0"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg></span>
                <span className="flex-1 text-sm font-medium">Account</span>
                <span className="ml-auto text-gray-300 [&_svg]:w-4 [&_svg]:h-4"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg></span>
              </Link>
              <div
                className="flex items-center gap-3.5 py-3.5 px-3 rounded-md text-gray-700 text-[15px] font-medium tracking-wide no-underline cursor-pointer active:bg-red-50"
                onClick={() => handleLogout()}
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 border border-red-200 shrink-0 text-red-500 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:m-0">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
                </span>
                <span className="flex-1 text-sm font-medium">Log Out</span>
              </div>
            </nav>
          </div>
        </>
      )}

      <PartnerAuthModals
        isLoginModalOpen={isLoginModalOpen}
        setIsLoginModalOpen={setIsLoginModalOpen}
        isRegisterModalOpen={isRegisterModalOpen}
        setIsRegisterModalOpen={setIsRegisterModalOpen}
        loginType={loginType}
        setLoginType={setLoginType}
        location={home}
      />
    </div>
  );
};

export default PartnerMobileBar;
